import { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  MenuItem,
  Menu,
  Tooltip,
} from "@mui/material";
import { Menu as MenuIcon, AccountCircle } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PostAddIcon from '@mui/icons-material/PostAdd';
import SaveIcon from '@mui/icons-material/Save';

interface Props {
  sidebarOpen: boolean;
  handleSidebarToggle: () => void;
  userRole: string;
  handleLogout: () => void;
  // For company details page
  companyId: number;
}

function NavBar({
  sidebarOpen,
  handleSidebarToggle,
  userRole,
  handleLogout,
  companyId,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  // For storing the route
  const routes = {
    software: "/software",
    company: "/company",
    subscription: "/subscription",
    user: "/user",
    log: "/logs",
    dbBackup: "/database-backups",
  };

  // For checking which path is active now
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // style for the sidebar list button when active
  const activeButtonStyles = {
    "&.Mui-selected": {
      backgroundColor: "rgba(25, 118, 210, 0.08)",
      "& .MuiListItemIcon-root": {
        color: "primary.main",
      },
    },
  };

  // Handle account menu
  const handleAccountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate("/profile");
    handleAccountClose();
    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  };

  function handleSoftwareButtonOnClick() {
    navigate("/softwares");

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  function handleApartmentButtonOnClick() {
    navigate("/company");

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  function handleSubscriptionButtonOnClick() {
    navigate("/subscription");

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  function handleUserButtonOnClick() {
    navigate("/user");

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  function handleLogsButtonOnClick() {
    navigate("/logs");

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  function handleDBBackupButtonOnClick() {
    navigate("/database-backups");

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  // For Admin level company button
  function handleCompanyDetailButtonOnClick() {
    navigate(`/company/details/${companyId}`);

    // Make sure sidebar close when click the button
    if (sidebarOpen) {
      handleSidebarToggle();
    }
  }

  return (
    <>
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: "white",
          color: "black",
          boxShadow: "none",
          borderBottom: "1px solid #ddd",
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="menu"
            onClick={handleSidebarToggle}
            sx={{ mr: 2, color: "black" }}
          >
            <MenuIcon />
          </IconButton>

          {/* Replace with logo later */}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            UAS License Portal
          </Typography>

          {/* Account Icon with Dropdown */}
          <Tooltip title="Setting">
            <IconButton onClick={handleAccountClick} sx={{ color: "black" }}>
              <AccountCircle />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={accountMenuOpen}
            onClose={handleAccountClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <ManageAccountsIcon sx={{ mr: 1 }}></ManageAccountsIcon> Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToAppIcon sx={{ mr: 1 }}></ExitToAppIcon>Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Side bar Design */}
      <Drawer
        variant="permanent"
        open={sidebarOpen}
        sx={{
          width: sidebarOpen ? 240 : 60,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sidebarOpen ? 240 : 60,
            transition: "width 0.3s",
            overflowX: "hidden",
          },
        }}
      >
        {/* Prevent the button from the side bar overlap with the navigation bar */}
        <Toolbar />
        {/* Item in the side bar */}
        <List>
          {/* Button to shown for Super Admin level */}
          {userRole === "Super Admin" && (
            <>
              {/* Software button, show Software dashboard when click */}
              <Tooltip title="Software" followCursor placement="right">
                <ListItemButton
                  selected={isActive(routes.software)}
                  onClick={handleSoftwareButtonOnClick}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={isActive(routes.software) ? "primary" : "inherit"}
                  >
                    <SportsEsportsIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Software" />}
                </ListItemButton>
              </Tooltip>

              {/* Company button, show company dashboard with users when click */}
              <Tooltip title="Company" followCursor placement="right">
                <ListItemButton
                  onClick={handleApartmentButtonOnClick}
                  selected={isActive(routes.company)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={isActive(routes.company) ? "primary" : "inherit"}
                  >
                    <ApartmentIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Company" />}
                </ListItemButton>
              </Tooltip>

              {/* Subscription button, show subscription dashboard when click */}
              <Tooltip title="Subscription" followCursor placement="right">
                <ListItemButton
                  onClick={handleSubscriptionButtonOnClick}
                  selected={isActive(routes.subscription)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={
                      isActive(routes.subscription) ? "primary" : "inherit"
                    }
                  >
                    <SubscriptionsIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Subscription" />}
                </ListItemButton>
              </Tooltip>

              {/* User button, show User dashboard when click */}
              <Tooltip title="User" followCursor placement="right">
                <ListItemButton
                  onClick={handleUserButtonOnClick}
                  selected={isActive(routes.user)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={isActive(routes.user) ? "primary" : "inherit"}
                  >
                    <PeopleAltIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="User" />}
                </ListItemButton>
              </Tooltip>

              {/* logs button, show Log dashboard when click */}
              <Tooltip title="Log" followCursor placement="right">
                <ListItemButton
                  onClick={handleLogsButtonOnClick}
                  selected={isActive(routes.log)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={isActive(routes.log) ? "primary" : "inherit"}
                  >
                    <PostAddIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Log" />}
                </ListItemButton>
              </Tooltip>

              {/* DB Backup button, show DB Backup dashboard when click */}
              <Tooltip title="Database Backups" followCursor placement="right">
                <ListItemButton
                  onClick={handleDBBackupButtonOnClick}
                  selected={isActive(routes.dbBackup)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={isActive(routes.dbBackup) ? "primary" : "inherit"}
                  >
                    <SaveIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Database Backups" />}
                </ListItemButton>
              </Tooltip>
            </>
          )}
          {userRole === "Admin" && (
            <>
              {/* Company button, show company dashboard with users when click */}
              <Tooltip title="Company" followCursor placement="right">
                <ListItemButton
                  onClick={handleCompanyDetailButtonOnClick}
                  selected={isActive(routes.company)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={isActive(routes.company) ? "primary" : "inherit"}
                  >
                    <ApartmentIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Company" />}
                </ListItemButton>
              </Tooltip>
            </>
          )}

          {userRole === "User" && (
            <>
              {/* Subscription button, show subscription dashboard when click */}
              <Tooltip title="Subscription" followCursor placement="right">
                <ListItemButton
                  onClick={handleSubscriptionButtonOnClick}
                  selected={isActive(routes.subscription)}
                  sx={activeButtonStyles}
                >
                  <ListItemIcon
                    color={
                      isActive(routes.subscription) ? "primary" : "inherit"
                    }
                  >
                    <SubscriptionsIcon />
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary="Subscription" />}
                </ListItemButton>
              </Tooltip>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
}

export default NavBar;
